import React, { useState, useEffect } from 'react';
import { Bell, CheckCircle, AlertTriangle, Clock, Filter, ChevronDown } from 'lucide-react';
import { getAlerts, updateReviewStatus } from '../services/api';

const statusOptions = [
  { value: 'recommended', label: 'Recommended', class: 'badge-success' },
  { value: 'pharmacist_review', label: 'Pharmacist Review', class: 'badge-warning' },
  { value: 'clinical_review', label: 'Clinical Review', class: 'badge-danger' },
  { value: 'not_recommended', label: 'Not Recommended', class: 'badge-neutral' },
  { value: 'pending', label: 'Pending', class: 'badge-info' }
];

function AlertCard({ alert, onStatusUpdate }) {
  const [isOpen, setIsOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      await onStatusUpdate(alert.id, newStatus);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setUpdating(false);
    }
  };

  const getStatusOption = (status) => statusOptions.find(o => o.value === status) || statusOptions[4];
  const currentStatus = getStatusOption(alert.review_status);

  return (
    <div className="card-premium p-5" data-testid={`alert-card-${alert.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-slate-900">{alert.name}</h3>
          <p className="text-sm text-slate-500">{alert.manufacturer}</p>
        </div>
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`${currentStatus.class} flex items-center gap-1`}
            disabled={updating}
          >
            {updating ? 'Updating...' : currentStatus.label}
            <ChevronDown className="w-3 h-3" />
          </button>
          
          {isOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 z-10 animate-fade-in">
              {statusOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => handleStatusChange(option.value)}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 first:rounded-t-lg last:rounded-b-lg"
                >
                  <span className={option.class}>{option.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
        <span>{alert.dosage_form}</span>
        <span>•</span>
        <span>{alert.strength}</span>
        <span>•</span>
        <span>Confidence: {alert.confidence}%</span>
      </div>

      {alert.risk_flags?.filter(Boolean).length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
            <AlertTriangle className="w-4 h-4" />
            Risk Considerations
          </div>
          <ul className="mt-2 space-y-1">
            {alert.risk_flags.filter(Boolean).map((flag, idx) => (
              <li key={idx} className="text-sm text-amber-600">• {flag}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`text-sm ${
            alert.review_status === 'clinical_review' ? 'text-red-600' :
            alert.review_status === 'pharmacist_review' ? 'text-amber-600' :
            'text-slate-500'
          }`}>
            {alert.review_status === 'clinical_review' ? 'Requires clinical governance review' :
             alert.review_status === 'pharmacist_review' ? 'Requires pharmacist review' :
             'Status: ' + (alert.review_status?.replace('_', ' ') || 'Pending')}
          </span>
        </div>
        <span className="text-xs text-slate-400">
          Est. savings: {Math.round((alert.estimated_savings_percent || 0) * 100)}%
        </span>
      </div>
    </div>
  );
}

export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    async function fetchAlerts() {
      try {
        const res = await getAlerts();
        setAlerts(res.data.alerts || []);
      } catch (error) {
        console.error('Failed to fetch alerts:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchAlerts();
  }, []);

  const handleStatusUpdate = async (genericId, status) => {
    try {
      await updateReviewStatus(genericId, status);
      setAlerts(prev => prev.map(a => 
        a.id === genericId ? { ...a, review_status: status } : a
      ));
    } catch (error) {
      throw error;
    }
  };

  const filteredAlerts = filter === 'all' 
    ? alerts 
    : alerts.filter(a => a.review_status === filter);

  const alertCounts = {
    clinical_review: alerts.filter(a => a.review_status === 'clinical_review').length,
    pharmacist_review: alerts.filter(a => a.review_status === 'pharmacist_review').length,
    all: alerts.length
  };

  return (
    <div className="p-6" data-testid="alerts-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-2">Review Queue</h1>
          <p className="text-slate-500">Manage substitution opportunities requiring review</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="badge-danger">{alertCounts.clinical_review} Clinical</span>
          <span className="badge-warning">{alertCounts.pharmacist_review} Pharmacist</span>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 mb-6 border-b border-slate-200">
        {[
          { value: 'all', label: 'All', count: alertCounts.all },
          { value: 'clinical_review', label: 'Clinical Review', count: alertCounts.clinical_review },
          { value: 'pharmacist_review', label: 'Pharmacist Review', count: alertCounts.pharmacist_review }
        ].map(tab => (
          <button
            key={tab.value}
            onClick={() => setFilter(tab.value)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              filter === tab.value 
                ? 'border-[#0A2540] text-[#0A2540]' 
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`filter-${tab.value}`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Alerts Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="card-premium p-5">
              <div className="skeleton h-6 w-48 mb-2" />
              <div className="skeleton h-4 w-32 mb-4" />
              <div className="skeleton h-16 w-full mb-4" />
              <div className="skeleton h-4 w-24" />
            </div>
          ))}
        </div>
      ) : filteredAlerts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredAlerts.map(alert => (
            <AlertCard 
              key={alert.id} 
              alert={alert} 
              onStatusUpdate={handleStatusUpdate}
            />
          ))}
        </div>
      ) : (
        <div className="card-premium p-12 text-center">
          <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">No items in review queue</p>
          <p className="text-sm text-slate-500 mt-1">All substitution opportunities have been processed</p>
        </div>
      )}
    </div>
  );
}
